import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AvdbTypeWithIssues } from '../models/avdb/avdb-type.model';
import { DeviceModel } from '../models/device/device-model.model';
import { Device } from '../models/device/device.model';
import { DeviceCommonService } from './device-common.service';

const apiURL = `${environment.flyLegacyUrl}/api/`;
const portablesURL = `${apiURL}portables/`;
const devicesURL = `${apiURL}devices/`;
const deviceModelsApiURL = `${apiURL}device-models/`;
const avdbsTypesURL = `${apiURL}/avdb-types/`;

const agentApiURL = `${apiURL}agent/`;
const agentDevicesURL = `${agentApiURL}devices/`;
const agentDevicesSearchURL = `${agentApiURL}search/devices/`;

const oemManagedDeviceUrl = `${apiURL}oemManaged/devices/`;

@Injectable({
  providedIn: 'root'
})
export class DeviceService extends DeviceCommonService {
  constructor(http: HttpClient) {
    super(http);
  }

  getPortables(withAvdbs: boolean = false): Observable<Device[]> {
    const params = {
      withAvdbs,
      withJeppImported: false
    };
    const portables = this.http
      .get<Device[]>(portablesURL, { params })
      .pipe(map((portablesA: Device[]) => portablesA.sort((a, b) => a.name.localeCompare(b.name))));
    return this.linkDeviceModels(portables);
  }

  addPortable(device: AddDevice): Observable<any> {
    return this.http.post(portablesURL, device);
  }

  getDevices(withAvdbs: boolean = false): Observable<Device[]> {
    const params = {
      withAvdbs,
      withJeppImported: false
    };
    const devices = this.http.get<Device[]>(devicesURL, { params });
    return this.linkDeviceModels(devices);
  }

  getDevicesByModel(model: DeviceModel, withAvdbs: boolean = false): Observable<Device[]> {
    const params = {
      modelID: model.id,
      withAvdbs,
      withJeppImported: false
    };
    const devices = this.http.get<Device[]>(devicesURL, { params });
    return this.linkDeviceModels(devices);
  }

  getDevice(
    deviceId: number,
    agentMode: boolean = false,
    withSharedAircraftDevice: boolean = false
  ): Observable<Device> {
    const params = {
      withSharedAircraftDevice
    };
    const device = this.http.get<Device>(`${agentMode ? agentDevicesURL : devicesURL}${deviceId}/`, { params });
    return this.linkDeviceModels(device.pipe(map((d) => [d]))).pipe(map((ds) => ds[0]));
  }

  getOemManagedDevice(deviceId: number): Observable<Device> {
    const device = this.http.get<Device>(`${oemManagedDeviceUrl}${deviceId}/`);
    return this.linkDeviceModels(device.pipe(map((d) => [d]))).pipe(map((ds) => ds[0]));
  }

  addDevice(device: AddDevice, aircraftID?: string): Observable<any> {
    return aircraftID != null
      ? this.http.post(devicesURL, device, { params: new HttpParams().set('aircraftID', aircraftID) })
      : this.http.post(devicesURL, device);
  }

  updateDevice(device: UpdateDevice): Observable<any> {
    const url = `${devicesURL}`;
    return this.http.put(url, device);
  }

  getAvdbTypesWithIssues(): Observable<AvdbTypeWithIssues[]> {
    return this.http.get<AvdbTypeWithIssues[]>(`${avdbsTypesURL}?withIssues=true`);
  }

  getDeviceInstructions(deviceId: number): Observable<string> {
    return this.http
      .get(`${deviceModelsApiURL}${deviceId}/serial-instructions`)
      .pipe(map((response) => String(response)));
  }

  createAddPortableURL(): Observable<string> {
    const url = `${environment.flyLegacyUrl}/api/add-portable/`;
    return this.http.post(url, null).pipe(map((res) => JSON.parse(res.toString()).json().launchURL));
  }

  agentSearchDevices(deviceSerial: string, withAvdbs: boolean = false): Observable<Device[]> {
    const params = {
      withAvdbs
    };
    return this.http.get<Device[]>(`${agentDevicesSearchURL}${deviceSerial}/`, { params });
  }

  upgradeTerrainVersion(deviceId: number): Observable<any> {
    let url = `${devicesURL}`;
    if (deviceId) {
      url += `upgrade/terrain-region?deviceId=${deviceId}`;
    }
    return this.http.post(url, null);
  }

  ignoreDatabase(deviceId: number, seriesId: number): Observable<any> {
    const url = `${environment.flyLegacyUrl}/api/devices/${deviceId}/customerAvdbs/${seriesId}`;
    const params = {
      critical: false
    };
    return this.http.put(url, '', { params });
  }
}

export interface AddDevice {
  name: string;
  modelID: number;
  serial: string;
}

export interface UpdateDevice extends AddDevice {
  id: number;
}
